import { useCollapsedActions } from '@components/MainStage/collapsedLayers.store';
import useCollapseController from '@components/MainStage/hooks/useCollapseController';
import useStore from '@components/MainStage/store';
import { subLayerParent, SubLayerTypes } from '@constants/canvas/layers';
import { ProjectDrawerTypes } from '@constants/entities/drawers';
import { LoadingKeys } from '@constants/entities/ui';
import { useProject } from '@context/Project';
import useLoading from '@hooks/useLoading';
import { canvasMap } from '@utils/canvas/CanvasMap';
import { collapseRelationController } from '@utils/canvas/CollapseRelationController';

const useOpenRelation = () => {
  const { setDrawer } = useProject();
  const { clearLocalEdges, clearLocalNodes, edges, selectEdge } = useStore();

  const { isCollapsed, expand } = useCollapsedActions();
  const { expandLayer } = useCollapseController();
  const { beginLoading } = useLoading();

  const handleCheckCollapsed = async (nodeId: string) => {
    const endLoading = beginLoading(LoadingKeys.Collapse);

    try {
      const subLayer = canvasMap.nodeMap.get(nodeId)
        ?.parentNode as SubLayerTypes;

      const parentLayer = subLayerParent[subLayer as SubLayerTypes];

      if (isCollapsed(parentLayer) || isCollapsed(subLayer)) {
        expand(subLayer);
        expand(parentLayer);

        await expandLayer([parentLayer, subLayer]);
        await collapseRelationController.renderPhantoms();
      }
    } finally {
      endLoading();
    }
  };

  const handleOpenRelation = (currentId: string, relatedId: string) => {
    const edgeId = edges.find(
      ({ source, target }) =>
        (source === currentId && target === relatedId) ||
        (source === relatedId && target === currentId),
    )?.id;

    if (!edgeId) return;

    clearLocalEdges();
    clearLocalNodes();

    canvasMap.resetFocusedNode();

    handleCheckCollapsed(currentId);
    handleCheckCollapsed(relatedId);

    selectEdge(edgeId);
    setDrawer({ type: ProjectDrawerTypes.EditRelationship });
  };

  return { handleOpenRelation };
};

export default useOpenRelation;
